/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorological Institute (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Selection } from 'd3';
import {
  select,
  group,
  max,
  scaleBand,
  scaleLinear,
  scaleSequentialLog,
  line as d3_line,
  curveMonotoneX,
  utcFormat,
  pointer,
  axisBottom,
  axisLeft,
  format,
} from 'd3';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Coverage, PointSeriesDomain } from '@opengeoweb/shared';
import { densityToBirdTAM, birdTAMToDensity } from '../utils/birdTAM';
import {
  birdTAMColorScale,
  interpolateBioRadColorScale,
} from '../utils/colorScales';
import {
  useFlySafeTranslation,
  translateKeyOutsideComponents,
} from '../utils/i18n';
import { getPlotMargins } from '../utils/plotLayout';
import {
  DAY_MS,
  HOUR_MS,
  FIVE_MIN_MS,
  buildContinuousTimes,
  closestTimeBand,
} from '../utils/timeUtils';
import type { Mode, FlySafeRow } from '../utils/types';

interface IntegratedDensityProps {
  id: number;
  forecastData?: FlySafeRow[] | null;
  observedData?: FlySafeRow[] | null;
  timeWindow?: [Date, Date];
  mode?: Mode;
  szaCoverage?: Coverage<PointSeriesDomain>;
  mapTime?: Date | null;
  onTimeSelect?: (time: Date) => void;
  preferredHeight: string;
  stationId?: string;
}

interface Row {
  datetime: Date;
  height_m: number;
  dens_km3: number;
}

function cleanup(node: HTMLDivElement | null): void {
  if (node) {
    select(node).selectAll('*').remove();
  }
}

// We draw the BirdTAM bands as background
interface Band {
  v0: number;
  v1: number;
  lvl: number;
  vMid: number;
}
const BIRDTAM_EDGES = [0, 0.625, 1.25, 2.5, 5, 10, 20, 40, 80];

function buildBirdTAMBands(yMaxNice: number): Band[] {
  const yTop = Math.max(0, yMaxNice);
  const bands: Band[] = [];

  let prev = 0;

  for (let i = 1; i < BIRDTAM_EDGES.length; i += 1) {
    const e = BIRDTAM_EDGES[i];
    const next = Math.min(e, yTop);
    if (next > prev) {
      const vMid = (prev + next) / 2;
      bands.push({ v0: prev, v1: next, lvl: densityToBirdTAM(vMid), vMid });
      prev = next;
    }
    if (prev >= yTop) {
      break;
    }
  }

  if (prev < yTop) {
    const vMid = (prev + yTop) / 2;
    bands.push({ v0: prev, v1: yTop, lvl: densityToBirdTAM(vMid), vMid });
  }

  if (bands.length === 0) {
    bands.push({ v0: 0, v1: 0, lvl: 0, vMid: 0 });
  }

  return bands;
}

// We get a series from the SZA coverage data
export const extractSzaSeries = (
  cov?: Coverage<PointSeriesDomain>,
): { ms: number; sza: number }[] => {
  if (!cov) {
    return [];
  }

  const tVals = cov.domain.axes.t.values as string[];
  if (!tVals.length) {
    return [];
  }

  const { ranges } = cov;

  const rangeObj = ranges.solarterminator ?? Object.values(ranges)[0];
  if (!rangeObj) {
    return [];
  }

  const vals = rangeObj.values as (number | null)[];
  if (!vals?.length) {
    return [];
  }

  const out: { ms: number; sza: number }[] = [];
  const num = Math.min(tVals.length, vals.length);

  for (let i = 0; i < num; i += 1) {
    const ms = new Date(tVals[i]).getTime();
    const val = vals[i];

    if (!Number.isFinite(ms)) {
      // eslint-disable-next-line no-continue
      continue;
    }
    if (val == null) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const sza = Number(val);
    if (!Number.isFinite(sza)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    out.push({ ms, sza });
  }

  out.sort((one, other) => one.ms - other.ms);
  return out;
};

// Lookup function that returns an interpolated SZA value for any timestamp
// We assume that points is sorted by ms ascending
const makeInterpolatedLookup = (points: { ms: number; sza: number }[]) => {
  if (!points.length) {
    return (_t: Date): number | null => null;
  }

  return (t: Date): number | null => {
    const target = t.getTime();
    if (!Number.isFinite(target)) {
      return null;
    }

    // Clamp  for extreme values (outside range limits)
    if (target <= points[0].ms) {
      return points[0].sza;
    }
    if (target >= points[points.length - 1].ms) {
      return points[points.length - 1].sza;
    }

    // Binary search to find surrounding indices
    let lo = 0;
    let hi = points.length - 1;
    while (lo <= hi) {
      // eslint-disable-next-line no-bitwise
      const mid = (lo + hi) >> 1;

      if (points[mid].ms < target) {
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    // points[lo - 1].ms < target <= points[lo].ms
    const p0 = points[lo - 1];
    const p1 = points[lo];

    // Interpolation fraction
    const frac = (target - p0.ms) / (p1.ms - p0.ms);

    return p0.sza + frac * (p1.sza - p0.sza);
  };
};

const IntegratedDensity = (props: IntegratedDensityProps): ReactNode => {
  const {
    id,
    forecastData,
    observedData,
    timeWindow,
    mode = 'birdtam',
    szaCoverage,
    mapTime,
    onTimeSelect,
    preferredHeight,
    stationId,
  } = props;

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const onTimeSelectRefStable = useRef(onTimeSelect);
  onTimeSelectRefStable.current = onTimeSelect;

  const mountRef = useRef<HTMLDivElement>(null);

  // Observe available width + height
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return (): void => {};
    }

    const update = (): void => {
      const rect = el.getBoundingClientRect();
      setSize({
        width: Math.floor(rect.width || 0),
        height: Math.floor(rect.height || 0),
      });
    };

    update();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (window as any).ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return (): void => window.removeEventListener('resize', update);
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        setSize({
          width: Math.floor(rect?.width ?? 0),
          height: Math.floor(rect?.height ?? 0),
        });
      }, 150);
    });

    ro.observe(el);
    return (): void => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      ro.disconnect();
    };
  }, []);

  const depsKey = useMemo(() => {
    const rLen = forecastData?.length ?? 0;
    const oLen = observedData?.length ?? 0;
    const tw0 = timeWindow?.[0]?.getTime() ?? 0;
    const tw1 = timeWindow?.[1]?.getTime() ?? 0;

    const szaPoints = extractSzaSeries(szaCoverage);
    const szaKey =
      szaPoints.length > 0
        ? `sza=${szaPoints.length}|${szaPoints[0].ms}-${szaPoints[szaPoints.length - 1].ms}`
        : 'sza=none';

    return `${id}|${tw0}-${tw1}|${rLen}|${oLen}|${mode}|w=${size.width}|h=${size.height}|theme=${theme.palette.mode}|${szaKey}`;
  }, [
    id,
    timeWindow,
    forecastData,
    observedData,
    mode,
    size.width,
    size.height,
    theme.palette.mode,
    szaCoverage,
  ]);

  // Ref to hold chart internals needed for the indicator line
  const chartRef = useRef<{
    gPlot: Selection<SVGGElement, unknown, null, undefined>;
    xCenter: (t: Date) => number;
    times: Date[];
    innerHeight: number;
  } | null>(null);

  useEffect(() => {
    const node = mountRef.current;
    if (!node) {
      return (): void => {};
    }

    cleanup(node);

    // Baseline for scaling
    const BASE_WIDTH = 1500;

    const width = Math.max(320, Math.floor(size.width || BASE_WIDTH));
    const height = Math.max(180, Math.floor(size.height || 260));

    const uiScale = Math.max(0.6, Math.min(1.2, width / BASE_WIDTH));
    const TICK_FONT = Math.max(10, Math.round(16 * uiScale));
    const TITLE_FONT = Math.max(10, Math.round(16 * uiScale));
    const TIP_FONT = Math.max(10, Math.round(12 * uiScale));

    const margin = getPlotMargins(uiScale);

    const innerWidth = Math.max(10, width - margin.left - margin.right);
    const innerHeight = Math.max(10, height - margin.top - margin.bottom);

    const DAYBAND_H = Math.max(12, Math.round(16 * uiScale)); // for solar zenith angle
    const AXIS_GAP = Math.max(4, Math.round(6 * uiScale));
    const plotHeight = Math.max(20, innerHeight - (DAYBAND_H + AXIS_GAP));
    const dayBandY = plotHeight;

    const svg = select(node)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('display', 'block')
      .style('width', '100%')
      .style('height', '100%')
      .style('touch-action', 'none');

    if (
      (!forecastData || forecastData.length === 0) &&
      (!observedData || observedData.length === 0)
    ) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', TIP_FONT)
        .attr('fill', '#64748b')
        .text('No data');
      return (): void => cleanup(node);
    }

    const parseRows = (input?: FlySafeRow[] | null): Row[] => {
      if (!input) {
        return [];
      }
      return input
        .map((row) => ({
          datetime: new Date(row.datetime),
          height_m: row.height,
          dens_km3: Math.max(0, row.dens ?? NaN),
        }))
        .filter(
          (dat) =>
            Number.isFinite(dat.height_m) &&
            Number.isFinite(dat.dens_km3) &&
            !Number.isNaN(dat.datetime.getTime()),
        );
    };

    let forecast = parseRows(forecastData);
    let observed = parseRows(observedData);

    if (timeWindow?.[0] && timeWindow?.[1]) {
      const [t0, t1] = timeWindow;
      forecast = forecast.filter(
        (dat) => dat.datetime >= t0 && dat.datetime <= t1,
      );
      observed = observed.filter(
        (dat) => dat.datetime >= t0 && dat.datetime <= t1,
      );
    }

    if (forecast.length === 0 && observed.length === 0) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', TIP_FONT)
        .attr('fill', '#64748b')
        .text('No data in range');
      return (): void => cleanup(node);
    }

    // VID integration (birds/km2)

    // Build two series (model and observations)
    const buildSeries = (input: Row[]): { t: Date; val: number }[] =>
      Array.from(
        group(input, (dat) => dat.datetime.getTime()),
        ([ms, arr]) => {
          const maxHraw = max(arr, (dat) => dat.height_m) ?? 0;
          const heightsInKm = maxHraw < 100;

          const rowsAtT = arr
            .slice()
            .map((row) => ({
              h_m: heightsInKm ? row.height_m * 1000 : row.height_m,
              dens_km3: row.dens_km3,
            }))
            .sort((one, other) => one.h_m - other.h_m);

          let vid = 0;
          for (let i = 0; i < rowsAtT.length - 1; i += 1) {
            const row = rowsAtT[i];
            const nextRow = rowsAtT[i + 1];
            const dzKm = (nextRow.h_m - row.h_m) / 1000;
            if (dzKm > 0) {
              vid += 0.5 * (row.dens_km3 + nextRow.dens_km3) * dzKm;
            }
          }

          return { t: new Date(Number(ms)), val: vid };
        },
      ).sort((one, other) => one.t.getTime() - other.t.getTime());

    const forecastSeries = buildSeries(forecast);
    const observedSeries = buildSeries(observed);

    if (forecastSeries.length === 0 && observedSeries.length === 0) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', TIP_FONT)
        .attr('fill', '#64748b')
        .text('No integrated values');
      return (): void => cleanup(node);
    }

    // This represents all the content
    const allSeries = [...forecastSeries, ...observedSeries].sort(
      (valA, valB) => valA.t.getTime() - valB.t.getTime(),
    );

    const dataSpanMs =
      allSeries.length > 0
        ? allSeries.at(-1)!.t.getTime() - allSeries[0].t.getTime()
        : 0;

    const multiDay = dataSpanMs > DAY_MS;
    const stepMs = multiDay ? HOUR_MS : FIVE_MIN_MS;

    const times =
      timeWindow?.[0] && timeWindow?.[1]
        ? buildContinuousTimes(timeWindow[0], timeWindow[1], stepMs)
        : buildContinuousTimes(allSeries[0].t, allSeries.at(-1)!.t, stepMs);

    // Build a map from data series for gap detection
    const forecastVidByMs = new Map(
      forecastSeries.map((point) => [point.t.getTime(), point.val]),
    );

    const observedVidByMs = new Map(
      observedSeries.map((point) => [point.t.getTime(), point.val]),
    );

    const fullForecastSeries = times.map((t) => ({
      t,
      val: forecastVidByMs.get(t.getTime()) ?? null,
    }));

    const fullObservedSeries = times.map((t) => ({
      t,
      val: observedVidByMs.get(t.getTime()) ?? null,
    }));

    const x = scaleBand<Date>().domain(times).range([0, innerWidth]);
    const bw = Math.max(1, x.bandwidth());
    const xCenter = (t: Date): number => (x(t) ?? 0) + bw / 2;

    const BIRDTAM_9_DENSITY = 160;
    const y = scaleLinear()
      .domain([0, BIRDTAM_9_DENSITY])
      .range([plotHeight, 0]);
    const yMaxNice = y.domain()[1];

    // eslint-disable-next-line id-length
    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Clip for plot area
    const clipId = `clip-vid-${id}`;
    g.append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('width', innerWidth)
      .attr('height', plotHeight);

    // BirdTAM bands
    const bands = buildBirdTAMBands(yMaxNice);

    const bioRadT = scaleSequentialLog().domain([1, 1000]);
    const bioRadForLevel = (lvl: number): string =>
      interpolateBioRadColorScale(bioRadT(birdTAMToDensity(lvl)));
    const bandFill = (lvl: number): string =>
      mode === 'birdtam' ? birdTAMColorScale(lvl) : bioRadForLevel(lvl);
    const LIGHT_OPACITY = 0.3;
    const DARK_OPACITY = 0.7;
    const bandOpacity = isDark ? DARK_OPACITY : LIGHT_OPACITY;
    const gBands = g
      .append('g')
      .attr('class', 'level-bands')
      .attr('clip-path', `url(#${clipId})`)
      .attr('pointer-events', 'none')
      .style('mix-blend-mode', isDark ? 'normal' : 'multiply');

    gBands
      .selectAll('rect')
      .data(
        bands.map((band) => {
          const yTop = y(band.v1);
          const yBottom = y(band.v0);
          return { ...band, y: yTop, height: Math.max(0, yBottom - yTop) };
        }),
      )
      .enter()
      .append('rect')
      .attr('x', 0)
      .attr('y', (dat) => dat.y)
      .attr('width', innerWidth)
      .attr('height', (dat) => dat.height)
      .attr('fill', (dat) => bandFill(dat.lvl))
      .attr('opacity', bandOpacity);

    const boundaryVals = BIRDTAM_EDGES.slice(1).filter(
      (val) => val > 0 && val < yMaxNice,
    );

    gBands
      .selectAll('line')
      .data(boundaryVals)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (val) => y(val))
      .attr('y2', (val) => y(val))
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 0.5)
      .attr('opacity', isDark ? 0.22 : 0.28)
      .attr('shape-rendering', 'crispEdges');

    // Line with actual VID values
    const LINE_STROKE = isDark ? '#e2e8f0' : '#0f172a';
    const line = d3_line<{ t: Date; val: number | null }>()
      .defined((dat) => dat.val !== null)
      .x((dat) => xCenter(dat.t))
      .y((dat) => y(dat.val ?? 0))
      .curve(curveMonotoneX);

    const gLine = g
      .append('g')
      .attr('class', 'vid-line')
      .attr('clip-path', `url(#${clipId})`);

    if (fullForecastSeries.some((data) => data.val !== null)) {
      gLine
        .append('path')
        .datum(fullForecastSeries)
        .attr('fill', 'none')
        .attr('stroke', LINE_STROKE)
        .attr('stroke-width', 2)
        .attr('d', line);
      if (fullObservedSeries.some((data) => data.val !== null)) {
        gLine
          .append('path')
          .datum(fullObservedSeries)
          .attr('fill', 'none')
          .attr('stroke', '#ef4444')
          .attr('stroke-width', 2)
          .attr('d', line);
      }
      // Find closest time band
      const nowMs = new Date().getTime();
      let closest = times[0];
      let closestDist = Math.abs(closest.getTime() - nowMs);
      for (const t of times) {
        const dist = Math.abs(t.getTime() - nowMs);
        if (dist < closestDist) {
          closest = t;
          closestDist = dist;
        }
      }

      const xPos = xCenter(closest);
      g.append('text')
        .attr('class', 'map-time-line')
        .attr('x', xPos)
        .attr('y', -4)
        .attr('text-anchor', 'middle')
        .attr('fill', '#ff0000')
        .attr('font-size', '12px')
        .attr('pointer-events', 'none')
        .text(translateKeyOutsideComponents('flysafe-now'));
      g.append('line')
        .attr('class', 'map-time-line')
        .attr('x1', xPos)
        .attr('x2', xPos)
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', '#ff0000')
        .attr('stroke-dasharray', 5.5)
        .attr('stroke-width', 2)
        .attr('pointer-events', 'none');
    } else {
      gLine
        .append('path')
        .datum(fullObservedSeries)
        .attr('fill', 'none')
        .attr('stroke', LINE_STROKE)
        .attr('stroke-width', 2)
        .attr('d', line);
    }

    // tooltip
    const makeTooltip = (): {
      showAt: (
        xpx: number,
        ypx: number,
        content: string,
        preferAbove?: boolean,
      ) => void;
      hide: () => Selection<SVGGElement, unknown, null, undefined>;
      raise: () => Selection<SVGGElement, unknown, null, undefined>;
    } => {
      const tt = g
        .append('g')
        .style('display', 'none')
        .attr('pointer-events', 'none');
      const bg = tt
        .append('rect')
        .attr('fill', 'white')
        .attr('stroke', '#94a3b8')
        .attr('rx', 4)
        .attr('ry', 4);
      const text = tt
        .append('text')
        .attr('font-size', TIP_FONT)
        .attr('fill', '#0f172a');
      return {
        showAt: (
          xpx: number,
          ypx: number,
          content: string,
          preferAbove = true,
        ): void => {
          text.text(content);

          const bb = (text.node() as SVGTextElement).getBBox();
          bg.attr('x', bb.x - 6)
            .attr('y', bb.y - 4)
            .attr('width', bb.width + 12)
            .attr('height', bb.height + 8);

          const tipW = bb.width + 12;
          const tipH = bb.height + 8;

          const xClamped = Math.max(
            4,
            Math.min(innerWidth - tipW - 4, xpx - tipW / 2),
          );

          const yTarget = preferAbove ? ypx - 10 : ypx + 10;
          const yClamped = Math.max(
            4,
            Math.min(innerHeight - tipH - 4, yTarget - tipH),
          );

          tt.style('display', null).attr(
            'transform',
            `translate(${xClamped},${yClamped})`,
          );
        },
        hide: () => tt.style('display', 'none'),
        raise: () => tt.raise(),
      };
    };

    const lineTip = makeTooltip();
    const szaTip = makeTooltip();

    // Solar terminator / solar zenith angle band from coverage
    const szaPoints = extractSzaSeries(szaCoverage);
    const szaForTime = makeInterpolatedLookup(szaPoints);

    const greyScale = scaleLinear()
      .domain([0, 90, 96, 102, 108, 180])
      .range([255, 255, 200, 140, 50, 0])
      .interpolate((valA, valB) => (ratio): number => {
        // smooth dusk with a gradient
        return valA + (valB - valA) * (ratio * ratio);
      })
      .clamp(true);

    const gDayNight = g.append('g').attr('class', 'daynight-band');

    const szaTimeFmt = utcFormat('%Y-%m-%d %H:%M');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const showSzaTip = (event: any, dat: { t: Date; sza: number }): void => {
      const [mx] = pointer(event, g.node() as SVGGElement);
      szaTip.raise();
      szaTip.showAt(
        mx,
        dayBandY,
        `${szaTimeFmt(dat.t)} (UTC) - Solar zenith angle (SZA): ${dat.sza.toFixed(0)}°`,
        true,
      );
    };

    gDayNight
      .selectAll('rect')
      .data(
        times
          .map((t) => {
            const sza = szaForTime(t);
            return sza == null ? null : { t, sza };
          })
          .filter((dat): dat is { t: Date; sza: number } => dat !== null),
      )
      .enter()
      .append('rect')
      .attr('x', (dat) => x(dat.t) ?? 0)
      .attr('y', dayBandY)
      .attr('width', bw)
      .attr('height', DAYBAND_H)
      .attr('fill', (dat) => {
        const gv = Math.round(greyScale(dat.sza));
        return `rgb(${gv},${gv},${gv})`;
      })
      .attr('shape-rendering', 'crispEdges')
      .on('pointerenter', (event, dat) => {
        showSzaTip(event, dat);
      })
      .on('pointermove', (event, dat) => {
        showSzaTip(event, dat);
      })
      .on('pointerleave', () => szaTip.hide());

    gDayNight
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', dayBandY)
      .attr('y2', dayBandY)
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', 1)
      .attr('opacity', 0.8)
      .attr('shape-rendering', 'crispEdges');

    // Axes
    const hourFormat = utcFormat('%H:%M');
    const dayFormat = utcFormat('%d %b');

    let xTickValues: Date[];

    if (!multiDay) {
      const xStep = Math.max(1, Math.ceil(times.length / 12));
      xTickValues = times.filter((_t, i) => i % xStep === 0);
    } else {
      xTickValues = times.filter((tickTime) => {
        const hour = tickTime.getUTCHours();
        const minute = tickTime.getUTCMinutes();
        return minute === 0 && (hour === 0 || hour === 12);
      });
    }

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        axisBottom<Date>(x)
          .tickValues(xTickValues)
          .tickFormat((dt) => {
            let label;
            if (!multiDay) {
              label = hourFormat(dt);
            } else if (dt.getUTCHours() === 0) {
              label = dayFormat(dt);
            } else {
              label = '12:00';
            }

            const isLastTick =
              dt.getTime() === xTickValues[xTickValues.length - 1].getTime();

            return isLastTick ? `${label} UTC` : label;
          }),
      );
    const gy = g.append('g').call(axisLeft<number>(y).ticks(6));
    gy.selectAll('text').style('font-size', `${TICK_FONT}px`);

    // Right axis: BirdTAM levels (to match the level bands)
    const rightAxis = g
      .append('g')
      .attr('class', 'birdtam-axis')
      .attr('transform', `translate(${innerWidth},0)`)
      .attr('pointer-events', 'none');

    // Domain line
    rightAxis
      .append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', plotHeight)
      .attr('stroke', LINE_STROKE)
      .attr('opacity', 0.35)
      .attr('shape-rendering', 'crispEdges');

    const tickLen = Math.max(5, Math.round(6 * uiScale));
    const tickPad = Math.max(3, Math.round(4 * uiScale));

    const rightTicks = rightAxis
      .selectAll('g.tick')
      .data(
        bands
          .map((band) => ({ val: band.v0, lvl: band.lvl }))
          .filter((dat) => dat.val >= 0 && dat.val <= yMaxNice && dat.lvl >= 4),
      )
      .enter()
      .append('g')
      .attr('class', 'tick')
      .attr('transform', (dat) => `translate(0,${y(dat.val)})`);

    rightTicks
      .append('line')
      .attr('x1', 0)
      .attr('x2', tickLen)
      .attr('y1', 0)
      .attr('y2', 0)
      .attr('stroke', LINE_STROKE)
      .attr('opacity', 0.6)
      .attr('shape-rendering', 'crispEdges');

    rightTicks
      .append('text')
      .attr('x', tickLen + tickPad)
      .attr('y', 0)
      .attr('dy', '0.32em')
      .style('font-size', `${TICK_FONT}px`)
      .style('fill', LINE_STROKE)
      .style('opacity', 0.9)
      .attr('text-anchor', 'start')
      .text((dat) => `${dat.lvl}`);

    svg
      .append('text')
      .attr('x', margin.left + innerWidth - 2)
      .attr('y', Math.max(12, margin.top - 6))
      .attr('text-anchor', 'end')
      .style('font-size', `${TITLE_FONT}px`)
      .style('fill', LINE_STROKE)
      .text('BirdTAM');

    svg
      .append('text')
      .attr(
        'transform',
        `translate(20, ${margin.top + plotHeight / 2}) rotate(-90)`,
      )
      .attr('text-anchor', 'middle')
      .attr('class', 'y-label')
      .style('font-size', `${TITLE_FONT}px`)
      .style('fill', LINE_STROKE)
      .text('(birds/km²)');

    // Vertical line for current map time
    if (mapTime && !isNaN(mapTime.getTime()) && times.length > 0) {
      const mapMs = mapTime.getTime();
      let closest = times[0];
      let closestDist = Math.abs(closest.getTime() - mapMs);
      for (const t of times) {
        const dist = Math.abs(t.getTime() - mapMs);
        if (dist < closestDist) {
          closest = t;
          closestDist = dist;
        }
      }
      const xPos = xCenter(closest);
      g.append('line')
        .attr('class', 'map-time-line')
        .attr('x1', xPos)
        .attr('x2', xPos)
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', '#ff0000')
        .attr('stroke-width', 2)
        .attr('pointer-events', 'none');
    }

    // Line tooltip
    const fmtInt = format(',.0f');
    const tf = utcFormat('%Y-%m-%d %H:%M');

    const indexFromPx = (px: number): number => {
      const i = Math.round(px / bw - 0.5);
      return Math.max(0, Math.min(times.length - 1, i));
    };

    const focus = g
      .append('g')
      .style('display', 'none')
      .attr('pointer-events', 'none');
    focus.append('circle').attr('r', 3.5).attr('fill', LINE_STROKE);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const showLineTipAt = (event: any): void => {
      const [mx] = pointer(event, g.node() as SVGGElement);
      const idx = indexFromPx(mx);

      const forecastPoint = fullForecastSeries[idx];
      const observedPoint = fullObservedSeries[idx];

      const hasForecast = forecastPoint?.val != null;
      const hasObserved = observedPoint?.val != null;

      if (!hasForecast && !hasObserved) {
        focus.style('display', 'none');
        lineTip.hide();
        return;
      }

      // Pick a value for positioning
      const basePoint = hasForecast ? forecastPoint : observedPoint;
      const yVal = basePoint!.val!;

      focus.style('display', null);
      focus
        .attr('transform', `translate(${xCenter(basePoint!.t)},${y(yVal)})`)
        .raise();

      // Build tooltip content
      let content = `${tf(basePoint!.t)} (UTC)`;

      if (hasForecast) {
        content += ` | Forecast: ${fmtInt(forecastPoint.val!)} birds/km²`;
      }

      if (hasObserved) {
        content += ` | Observed: ${fmtInt(observedPoint.val!)} birds/km²`;
      }

      lineTip.raise();
      lineTip.showAt(xCenter(basePoint!.t), y(yVal), content, true);
    };

    const hideLineTip = (): void => {
      focus.style('display', 'none');
      lineTip.hide();
    };

    // Time-select + tooltip overlay
    const onTimeSelectRef = onTimeSelectRefStable;
    let dragging = false;
    let lastDispatchMs = 0;
    const THROTTLE_MS = 60;

    const dispatchTime = (event: PointerEvent): void => {
      const cb = onTimeSelectRef.current;
      if (!cb) {
        return;
      }
      const [mx] = pointer(event, g.node() as SVGGElement);
      const hit = closestTimeBand(mx, times, (t) => x(t) ?? 0, bw);
      if (hit) {
        cb(hit);
      }
    };

    g.append('rect')
      .attr('fill', 'transparent')
      .attr('pointer-events', 'all')
      .attr('width', innerWidth)
      .attr('height', plotHeight)
      .on('pointerdown', (event: PointerEvent) => {
        dragging = true;
        lastDispatchMs = 0;
        dispatchTime(event);
        (event.target as Element)?.setPointerCapture?.(event.pointerId);
      })
      .on('pointermove', (event: PointerEvent) => {
        showLineTipAt(event);
        if (!dragging) {
          return;
        }
        const now = Date.now();
        if (now - lastDispatchMs < THROTTLE_MS) {
          return;
        }
        lastDispatchMs = now;
        dispatchTime(event);
      })
      .on('pointerup', () => {
        dragging = false;
      })
      .on('pointerleave', () => {
        dragging = false;
        hideLineTip();
      })
      .on('pointerenter', (event) => showLineTipAt(event));

    // At end, store refs:
    chartRef.current = { gPlot: g, xCenter, times, innerHeight };
    return (): void => {
      cleanup(node);
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  // Indicator line effect — lightweight, runs on every tick
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !mapTime || isNaN(mapTime.getTime())) {
      return;
    }
    const { gPlot, xCenter, times, innerHeight } = chart;

    // Remove old line
    gPlot.selectAll('.map-time-line').remove();

    if (times.length === 0) {
      return;
    }

    // Find closest time band
    const mapMs = mapTime.getTime();
    let closest = times[0];
    let closestDist = Math.abs(closest.getTime() - mapMs);
    for (const t of times) {
      const dist = Math.abs(t.getTime() - mapMs);
      if (dist < closestDist) {
        closest = t;
        closestDist = dist;
      }
    }

    const xPos = xCenter(closest);
    gPlot
      .append('line')
      .attr('class', 'map-time-line')
      .attr('x1', xPos)
      .attr('x2', xPos)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#ff0000')
      .attr('stroke-width', 2)
      .attr('pointer-events', 'none');
  }, [mapTime]);

  const { t } = useFlySafeTranslation();

  const titleTooltip = (
    <>
      <strong>{t('flysafe-tooltip-integrated-title')}</strong>{' '}
      {t('flysafe-tooltip-integrated-desc')}
    </>
  );

  const stationName = stationId
    ? t(`stations.${stationId}`, { defaultValue: stationId })
    : '';

  return (
    <div style={{ width: '100%' }}>
      <Tooltip title={titleTooltip}>
        <Typography color="text.secondary" variant="body1">
          {t('flysafe-integrated-density')}
          {stationName ? ` - ${stationName}` : ''}
        </Typography>
      </Tooltip>
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: `clamp(200px, ${preferredHeight}, 380px)`,
          minHeight: 0,
        }}
      >
        <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      </Box>
    </div>
  );
};
export default IntegratedDensity;
